import { areNavigationPathsAllowed, getNavigationPermissionPath } from './navigationSearch';

export interface StoredNavigationHistoryItem {
  id: string;
  title: string;
  url: string;
  permissionPaths: readonly string[];
  timestamp: number;
}

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

const HISTORY_KEY_PREFIX = 'pt_master_navigation_history:v2:';
export const LEGACY_RAW_SEARCH_HISTORY_KEY = 'pt_master_search_history';
const MAX_HISTORY_ITEMS = 10;

export function getNavigationHistoryStorageKey(userId: string): string {
  return `${HISTORY_KEY_PREFIX}${userId}`;
}

export function removeLegacyRawNavigationHistory(storage: StorageLike): void {
  try {
    storage.removeItem(LEGACY_RAW_SEARCH_HISTORY_KEY);
  } catch { /* storage unavailable */ }
}

function isHistoryItem(value: unknown): value is StoredNavigationHistoryItem {
  if (!value || typeof value !== 'object') return false;
  const item = value as Partial<StoredNavigationHistoryItem>;
  return typeof item.id === 'string'
    && typeof item.title === 'string'
    && typeof item.url === 'string'
    && typeof item.timestamp === 'number'
    && Array.isArray(item.permissionPaths)
    && item.permissionPaths.every((path) => typeof path === 'string');
}

export function loadNavigationHistory(
  storage: StorageLike,
  userId: string | null | undefined,
  allowedPaths: ReadonlySet<string>,
): StoredNavigationHistoryItem[] {
  // Keep cleanup idempotent for callers that load history directly. The
  // navigation store also performs this at module initialization so cleanup
  // does not depend on the command palette ever being opened.
  try {
    removeLegacyRawNavigationHistory(storage);
    if (!userId) return [];
    const key = getNavigationHistoryStorageKey(userId);
    const parsed: unknown = JSON.parse(storage.getItem(key) ?? '[]');
    const filtered = (Array.isArray(parsed) ? parsed : [])
      .filter(isHistoryItem)
      .filter((item) => areNavigationPathsAllowed(allowedPaths, item.permissionPaths))
      .slice(0, MAX_HISTORY_ITEMS);
    storage.setItem(key, JSON.stringify(filtered));
    return filtered;
  } catch {
    if (userId) {
      try { storage.removeItem(getNavigationHistoryStorageKey(userId)); } catch { /* unavailable */ }
    }
    return [];
  }
}

export function addNavigationHistoryItem(
  storage: StorageLike,
  userId: string | null | undefined,
  allowedPaths: ReadonlySet<string>,
  item: StoredNavigationHistoryItem,
): StoredNavigationHistoryItem[] {
  if (!userId || !areNavigationPathsAllowed(allowedPaths, item.permissionPaths)) return [];
  const history = loadNavigationHistory(storage, userId, allowedPaths);
  const normalized = {
    ...item,
    url: item.url,
    permissionPaths: item.permissionPaths.map(getNavigationPermissionPath),
  };
  const next = [normalized, ...history.filter((entry) => entry.id !== item.id)].slice(0, MAX_HISTORY_ITEMS);
  storage.setItem(getNavigationHistoryStorageKey(userId), JSON.stringify(next));
  return next;
}

export function clearNavigationHistory(storage: StorageLike, userId: string | null | undefined): void {
  try {
    removeLegacyRawNavigationHistory(storage);
    if (userId) storage.removeItem(getNavigationHistoryStorageKey(userId));
  } catch { /* storage unavailable */ }
}
